import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import API_URL from '../config/api.js';
import { useGooglePlacesAutocomplete } from '../hooks/useGooglePlaces';

function PartnerManagement() {
  const [partners, setPartners] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingPartner, setEditingPartner] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadPartners();
  }, []);

  const loadPartners = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/referrals/admin/partners`);
      setPartners(response.data.partners);
    } catch (error) {
      console.error('Error loading partners:', error);
      alert('Failed to load partners');
    } finally {
      setLoading(false);
    }
  };

  const openCreateModal = () => {
    setEditingPartner({
      contactName: '',
      contactEmail: '',
      contactPhone: '',
      address: '',
      city: '',
      state: '',
      zip: '',
      companyName: '',
      companyCode: '',
      logo: '',
      active: true,
    });
    setShowModal(true);
  };

  const openEditModal = (partner) => {
    setEditingPartner({ ...partner });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingPartner(null);
  };

  const savePartner = async (partnerData) => {
    try {
      if (partnerData._id) {
        // Update existing
        await axios.put(`${API_URL}/api/referrals/ners/${partnerData._id}`, partnerData);
      } else {
        // Create new
        await axios.post(`${API_URL}/api/referrals/admin/partners`, partnerData);
      }
      
      await loadPartners();
      closeModal();
      alert('Partner saved successfully!');
    } catch (error) {
      console.error('Error saving partner:', error);
      alert(error.response?.data?.error || 'Failed to save partner');
    }
  };

  const deletePartner = async (partnerId) => {
    if (!confirm('Are you sure you want to delete this partner? This cannot be undone.')) {
      return;
    }

    try {
      await axios.delete(`${API_URL}/api/referrals/admin/partners/${partnerId}`);
      await loadPartners();
      alert('Partner deleted successfully');
    } catch (error) {
      console.error('Error deleting partner:', error);
      alert('Failed to delete partner');
    }
  };

  const toggleActive = async (partnerId) => {
    try {
      await axios.patch(`${API_URL}/api/referrals/admin/partners/${partnerId}/toggle-active`);
      await loadPartners();
    } catch (error) {
      console.error('Error toggling partner:', error);
      alert('Failed to toggle partner status');
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Loading partners...</p>
      </div>
    );
  }

  return (
    <div className="partner-management">
      <div className="section-header">
        <h2>Partner Management</h2>
        <motion.button
          className="create-button"
          onClick={openCreateModal}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          + Add Partner
        </motion.button>
      </div>

      {partners.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">🤝</div>
          <h3>No partners yet</h3>
          <p>Add your first partner to get started with partner referrals</p>
        </div>
      ) : (
        <div className="partners-grid">
          {partners.map((partner) => (
            <motion.div
              key={partner._id}
              className="partner-card"
              layout
              whileHover={{ scale: 1.02 }}
            >
              <div className="partner-card-header">
                <div>
                  <h3>{partner.companyName}</h3>
                  <p className="company-code">/{partner.companyCode}</p>
                </div>
                <span className={`status-badge ${partner.active ? 'active' : 'inactive'}`}>
                  {partner.active ? '✓ Active' : '✕ Inactive'}
                </span>
              </div>

              <div className="partner-card-body">
                <div className="partner-info">
                  <p><strong>Contact:</strong> {partner.contactName}</p>
                  <p><strong>Email:</strong> {partner.contactEmail}</p>
                  <p><strong>Phone:</strong> {partner.contactPhone}</p>
                  <p><strong>Address:</strong> {partner.address}</p>
                  <p><strong>Referrals:</strong> {partner.totalReferrals}</p>
                </div>

                <div className="partner-card-actions">
                  <button
                    className="action-button edit"
                    onClick={() => openEditModal(partner)}
                  >
                    ✏️ Edit
                  </button>
                  <button
                    className={`action-button toggle ${partner.active ? 'deactivate' : 'activate'}`}
                    onClick={() => toggleActive(partner._id)}
                  >
                    {partner.active ? '⏸️ Deactivate' : '▶️ Activate'}
                  </button>
                  <button
                    className="action-button delete"
                    onClick={() => deletePartner(partner._id)}
                  >
                    🗑️ Delete
                  </button>
                </div>

                <div className="partner-url">
                  <small>Partner URL:</small>
                  <code>
                    ev.1-home.com/partner/{partner.companyCode}
                  </code>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      <AnimatePresence>
        {showModal && (
          <PartnerModal
            partner={editingPartner}
            onSave={savePartner}
            onCancel={closeModal}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

function PartnerModal({ partner, onSave, onCancel }) {
  const [formData, setFormData] = useState(partner);
  const [saving, setSaving] = useState(false);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await onSave(formData);
    } finally {
      setSaving(false);
    }
  };

  const handlePlaceSelected = (placeData) => {
    setFormData(prev => ({
      ...prev,
      address: placeData.address,
      city: placeData.city,
      state: placeData.state,
      zip: placeData.zip,
    }));
  };

  const { 
    inputRef: addressInputRef, 
    suggestions, 
    loading: loadingSuggestions, 
    showSuggestions, 
    handleSuggestionClick 
  } = useGooglePlacesAutocomplete(handlePlaceSelected);

  // Generate company code suggestion from company name
  const suggestCompanyCode = () => {
    if (formData.companyName && !formData.companyCode) {
      const suggested = formData.companyName
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
      setFormData(prev => ({ ...prev, companyCode: suggested }));
    }
  };

  return (
    <motion.div
      className="modal-overlay"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onCancel}
    >
      <motion.div
        className="modal-content large"
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        onClick={(e) => e.stopPropagation()}
      >
        <h3>{partner._id ? 'Edit Partner' : 'Add New Partner'}</h3>

        <form onSubmit={handleSubmit} className="partner-form">
          <div className="form-section">
            <h4>Contact Information</h4>
            <div className="form-grid">
              <div className="form-group">
                <label>Contact Name *</label>
                <input
                  type="text"
                  value={formData.contactName}
                  onChange={(e) => handleChange('contactName', e.target.value)}
                  placeholder="John Smith"
                  required
                />
              </div>

              <div className="form-group">
                <label>Email *</label>
                <input
                  type="email"
                  value={formData.contactEmail}
                  onChange={(e) => handleChange('contactEmail', e.target.value)}
                  placeholder="john@company.com"
                  required
                />
              </div>

              <div className="form-group">
                <label>Phone *</label>
                <input
                  type="tel"
                  value={formData.contactPhone}
                  onChange={(e) => handleChange('contactPhone', e.target.value)}
                  placeholder="(555) 123-4567"
                  required
                />
              </div>
            </div>
          </div>

          <div className="form-section">
            <h4>Address</h4>
            <div className="form-group full-width">
              <label>Street Address *</label>
              <div className="address-autocomplete">
                <input
                  ref={addressInputRef}
                  type="text"
                  value={formData.address}
                  onChange={(e) => handleChange('address', e.target.value)}
                  placeholder="Start typing address..."
                  required
                  autoComplete="off"
                />
                {loadingSuggestions && <span className="loading-indicator">Searching...</span>}
                {showSuggestions && suggestions.length > 0 && (
                  <div className="suggestions-dropdown">
                    {suggestions.map((suggestion) => (
                      <div
                        key={suggestion.place_id}
                        className="suggestion-item"
                        onClick={() => handleSuggestionClick(suggestion)}
                      >
                        {suggestion.description}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="form-grid">
              <div className="form-group">
                <label>City</label>
                <input
                  type="text"
                  value={formData.city}
                  onChange={(e) => handleChange('city', e.target.value)}
                  placeholder="Salt Lake City"
                />
              </div>

              <div className="form-group">
                <label>State</label>
                <input
                  type="text"
                  value={formData.state}
                  onChange={(e) => handleChange('state', e.target.value)}
                  placeholder="UT"
                  maxLength="2"
                />
              </div>

              <div className="form-group">
                <label>ZIP Code</label>
                <input
                  type="text"
                  value={formData.zip}
                  onChange={(e) => handleChange('zip', e.target.value)}
                  placeholder="84101"
                  maxLength="5"
                />
              </div>
            </div>
          </div>

          <div className="form-section">
            <h4>Company Information</h4>
            <div className="form-grid">
              <div className="form-group">
                <label>Company Name *</label>
                <input
                  type="text"
                  value={formData.companyName}
                  onChange={(e) => handleChange('companyName', e.target.value)}
                  onBlur={suggestCompanyCode}
                  placeholder="Acme Solar Inc"
                  required
                />
              </div>

              <div className="form-group">
                <label>Company Code *</label>
                <input
                  type="text"
                  value={formData.companyCode}
                  onChange={(e) => handleChange('companyCode', e.target.value.toLowerCase())}
                  placeholder="acme-solar"
                  pattern="[a-z0-9-]+"
                  required
                />
                <small style={{ color: '#666', marginTop: '4px', display: 'block' }}>
                  Lowercase letters, numbers, and hyphens only. Used in URL: /partner/{formData.companyCode}
                </small>
              </div>

              <div className="form-group">
                <label>Logo URL (Optional)</label>
                <input
                  type="url"
                  value={formData.logo}
                  onChange={(e) => handleChange('logo', e.target.value)}
                  placeholder="https://example.com/logo.png"
                />
              </div>
            </div>
          </div>

          <div className="form-section">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={formData.active}
                onChange={(e) => handleChange('active', e.target.checked)}
              />
              <span>Active (partner can receive referrals)</span>
            </label>
          </div>

          <div className="modal-actions">
            <button type="button" className="cancel-button" onClick={onCancel} disabled={saving}>
              Cancel
            </button>
            <button type="submit" className="save-button" disabled={saving}>
              {saving ? 'Saving...' : partner._id ? 'Update Partner' : 'Create Partner'}
            </button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
}

export default PartnerManagement;